import { createContext, useContext, useEffect, useState } from 'react';
import { initialFormState } from './constants';
import {
  fetchAvicChannels,
  fetchCountries,
  fetchSalesOrgs,
  getSubmitPayload,
  submitAvicChannels,
} from './helpers';
import { CostingRequestsContext } from '../../../contexts/CostingRequest';
import { showAlert } from '../../../utils/alerts';
import { Summary } from './summary';

export const AvicContext = createContext();

export const Provider = ({ children }) => {
  const { getSkus } = useContext(CostingRequestsContext);
  const [form, setForm] = useState(initialFormState);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [summary, setSummary] = useState({ status: false, details: undefined });
  const [selectedSku, setSelectedSku] = useState({});

  useEffect(() => {
    initialize();

    return () => reset();
  }, []);

  const initialize = async () => {
    try {
      const [countriesData, salesOrgsData] = await Promise.all([
        fetchCountries(),
        fetchSalesOrgs(),
        getSkus(),
      ]);

      setCountries(countriesData || []);
      setSalesOrgs(salesOrgsData || []);
    } catch (error) {
      console.error('Error initializing state:', error);
    }
  };

  const reset = () => {
    // setForm(initialFormState);
    setChannels([]);
  };

  const onFormChange = (key, value, sales_org) => {
    setForm({
      ...form,
      [key]: value,
      sales_org: sales_org ? sales_org : form.sales_org,
    });
  };

  const onChannelChange = (key, value, channelNumber) => {
    const currentChannels = [...channels];
    const index = currentChannels.findIndex(
      (channel) => channel.distribution_channel === channelNumber,
    );

    if (index > -1) {
      currentChannels[index][key] = value;
      if (key === 'cost_per_hl') currentChannels[index]['cost_per_100hl'] = value * 100;
      setChannels(currentChannels);
    }
  };

  const canDisplayAvic = () => form.country && form.sales_org && form.sku_number;

  const hasNewChannel = () => channels.filter((channel) => !channel.existing).length > 0;

  const hasChannel = () => channels.length > 0;

  const fetch = async () => {
    try {
      setLoading(true);
      const data = await fetchAvicChannels(form);
      setChannels(data.channels);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    try {
      setSaveLoading(true);
      const payload = getSubmitPayload(channels, countries, form.country);
      const data = await submitAvicChannels(payload);

      setSummary({ status: true, details: data });
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <AvicContext.Provider
      value={{
        form,
        onFormChange,
        canDisplayAvic,
        salesOrgs,
        countries,
        loading,
        fetch,
        submit,
        channels,
        onChannelChange,
        saveLoading,
        channels,
        hasNewChannel,
        hasChannel,
        selectedSku,
        setSelectedSku,
        reset,
      }}
    >
      {children}

      {summary.status && (
        <Summary
          open={summary.status}
          details={summary.details}
          setOpen={(status) => setSummary({ status, details: undefined })}
          reset={reset}
        />
      )}
    </AvicContext.Provider>
  );
};
